import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";

const cache = new Map();

/** URL-synced range/compare state -> one coordinated report fetch. */
export default function useContractReport() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const abortRef = useRef(null);

  const state = useMemo(() => ({
    range: params.get("range") || (params.get("from") ? null : "30d"),
    from: params.get("from"),
    to: params.get("to"),
    compare: params.get("compare") || "previous",
    compareFrom: params.get("compareFrom"),
    compareTo: params.get("compareTo"),
  }), [params]);

  const setState = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "" ||
          (k === "range" && v === "30d" && !patch.from) ||
          (k === "compare" && v === "previous")) next.delete(k);
      else next.set(k, v);
    });
    if (patch.range) { next.delete("from"); next.delete("to"); }
    if (patch.from) next.delete("range");
    if (patch.compare && patch.compare !== "custom") { next.delete("compareFrom"); next.delete("compareTo"); }
    setParams(next, { replace: true });
  }, [params, setParams]);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (state.range) q.set("range", state.range);
    if (state.from) q.set("dfrom", state.from);
    if (state.to) q.set("dto", state.to);
    q.set("compare", state.compare);
    if (state.compareFrom) q.set("compareFrom", state.compareFrom);
    if (state.compareTo) q.set("compareTo", state.compareTo);
    return q.toString();
  }, [state]);

  const load = useCallback(async (force = false) => {
    if (!force && cache.has(query)) {
      setData(cache.get(query));
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const r = await api.get(`/performance/contracts/report?${query}`, { signal: ctrl.signal });
      cache.set(query, r.data);
      setData(r.data);
      setError(false);
    } catch (e) {
      if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") setError(true);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  return { state, setState, data, loading, error, reload: () => load(true) };
}
